
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {Ingredient} from "@/models/Ingredient/Ingredient";
import {mapGetters, mapState} from "vuex";
import {NutrientType,} from "@/models/Ingredient/NutrientType";
import {UnitOption} from "@/models/Ingredient/UnitOption";
import {MergedNutrient, NutrientsGroup, NutrientUnitList} from "@/models/Ingredient/NutrientUnitList";
import {IngredientMeasurementEnum} from "@/models/Ingredient/IngredientMeasurementEnum";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import {debounce} from "@/utils/Helpers";
import vModal from "@/components/vModal.vue";
import VIngredientNutrientQuantityInputRow from "@/components/vIngredientNutrientQuantityInputRow.vue";
import vTabbedContent from "@/components/vTabbedContent.vue";
import {NUTRIENTS_FETCH} from "@/store/ActionTypes";
import {NutrientUnitWithValue} from "@/models/Nutrient/NutrientUnitWithValue";
import {
  OUNCE_TO_GRAMS,
  FLUID_OUNCE_TO_MILLILITERS
} from '@/utils/constants';
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {SavedAllergen} from "@/models/Allergen/SelectedAllergen";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";

type SelectObj = { id: number; name: string };
type DensityObj = { id: number; category: string };

interface ErrorStates {
  [key: number]: boolean;
}

@Component({
  components: {
    vModal,
    VUpsellModal,
    VIngredientNutrientQuantityInputRow,
    vTabbedContent,
    AllergensConfiguration
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
    ...mapGetters('nutrients', ['getNutrients']),
  },
})
export default class vIngredientManagement extends Vue {
  @Prop() editMode!: Boolean;
  @Prop() ingredient!: Ingredient;
  @Prop({ default: 0 }) postSaveAiApplyToken!: number;
  @Prop({ default: 0 }) postSavePropagationReviewToken!: number;

  selectedAllergens: SavedAllergen[] = [];
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  ingredientAllergenLanguage: keyof typeof LabelLanguage = "English";
  aiAutoApplyToken = 0;
  pendingAiAutoAnalyze = false;
  pendingAiAutoAnalyzeNotification = false;
  skipNextAiSaveBeforeOpen = false;

  weightMeasurments: SelectObj[] = [
    { id: IngredientMeasurementEnum.Gram, name: "g" },
    { id: IngredientMeasurementEnum.Milliliter, name: "ml" },
    { id: IngredientMeasurementEnum.Ounce, name: "oz" },
    { id: IngredientMeasurementEnum.FluidOunce, name: "fl. oz" },
  ];
  densityOptions: DensityObj[] = [];
  densityId: number | string = "";
  showWarningModal = false;
  showTrialUpsellModal = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  localizationState: any;
  accountState: any;
  categories: { id: number; name: string }[] = [];
  mergedNutrientGroup: { [category: string]: MergedNutrient[] } = {};
  nutrientSourceGroup : NutrientsGroup | null = null;
  errorStates: ErrorStates = {};

  provide() {
    return {
      bopFetchPreview: () => {},
    };
  }

  /**
   * Make ingredient enum available in template, the scope of the template is limited to the component instance members.
   */
  public get ingredientMeasurementEnum(): typeof IngredientMeasurementEnum {
    return IngredientMeasurementEnum;
  }

  get isTrialUser() {
    return this.accountState.userDetails.isTrial;
  }

  get allergenStatusLegendTooltipContent() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.translations.get("allergens_ai_legend_ai") || "AI detected this allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.translations.get("allergens_ai_legend_removed") || "Manually marked as not an allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.translations.get("allergens_ai_legend_user_added") || "Manually added as an allergen."}</span></div>`,
      "</div>"
    ].join("");
  }

  get isAllergenAiAddonActive() {
    if (this.isTrialUser) {
      return true;
    }

    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get showAllergenAiButton() {
    return this.isTrialUser || this.isAllergenAiAddonActive;
  }

  get shouldShowIngredientAiDetector() {
    return Boolean(this.ingredient?.id && !this.ingredient.hasAllergenAnalysis);
  }

  @Watch("postSaveAiApplyToken")
  onPostSaveAiApplyTokenUpdated(value: number) {
    if (!value || !this.pendingAiAutoAnalyze) {
      return;
    }

    this.pendingAiAutoAnalyze = false;
    this.pendingAiAutoAnalyzeNotification = true;
    this.skipNextAiSaveBeforeOpen = true;
    this.aiAutoApplyToken += 1;
  }

  async beforeOpenIngredientAiDetector() {
    if (this.skipNextAiSaveBeforeOpen) {
      this.skipNextAiSaveBeforeOpen = false;
      return true;
    }

    if (this.ingredient) {
      this.pendingAiAutoAnalyze = true;
      this.submit({ openAiAfterSave: true });
      return false;
    }

    return true;
  }

  /**
   * Method for fetching ingredient density options based on measurement
   */
  searchCategoryDensities(searchQuery: string) {
    // Check if the density category has changed and update the value so that it is available as a dropdown option
    this.densityId = this.ingredient.categoryDensityId
      ? this.ingredient.categoryDensityId
      : "";
    debounce(
      () => {
        IngredientsRepository.searchCategoryDensities(searchQuery, this.densityId)
          .then((response: any) => {
            this.densityOptions = response.data.result;
          })
          .catch((error) => {
            if (error.response) {
              this.$snotify.error(
                error.response.data.errorMessages[0].userVisibleMessage
              );
            }
            else this.$snotify.error(this.translations.get("network-error"));
          });
      },
      600,
      "search-category-densities"
    );
  }

  pushNutrient(nutrient: MergedNutrient, nutrientList: NutrientUnitWithValue[]) {
    if (nutrient.value) {
      nutrientList.push({
        value: nutrient.value,
        nutrientUnitId: nutrient.nutrientUnitId,
      });
    }

    // Handle children
    if (nutrient.childNutrients) {
      nutrient.childNutrients.forEach((childNutrient) => {
        this.pushNutrient(childNutrient, nutrientList);
      });
    }
  }

  submit(options?: { openAiAfterSave?: boolean }) {
    this.ingredient.allergens = [...this.selectedAllergens];
    this.ingredient.mayContainText = { ...this.mayContainText };
    const skipCalculationWarning = Boolean(options?.openAiAfterSave);

    // Create the nutrients array based on merged data
    const nutrients: NutrientUnitWithValue[] = [];

    let energyValue = 0;

    // Iterate through all categories and nutrients
    Object.values(this.mergedNutrientGroup).forEach((nutrientList) => {
      nutrientList.forEach((nutrient) => {
        if(nutrient.nutrientType == NutrientType.Energy){
          energyValue = nutrient.value;
        }

        this.pushNutrient(nutrient, nutrients);
      });
    });

    const sugarsValue = this.mergedNutrientGroup["sugars"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.Sugars
    );

    const vitaminAValue = this.mergedNutrientGroup["vitamins"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.VitaminA
    );

    const moistureValue = this.mergedNutrientGroup["others"].find(
      (nutrient) => nutrient.nutrientType === NutrientType.Moisture
    );

    if (sugarsValue.value < sugarsValue.childNutrients[0].value) {
      this.$snotify.error(
        this.translations.get("AddedSugarIsLessThanEqualToSugars")
      );
      return;
    }

    if(vitaminAValue.value < this.calculateTotalChildNutrientsValue(vitaminAValue.childNutrients)){
      this.$snotify.error(
        this.translations.get("VitaminAIsLessThanEqualToRetinolBetaCarotene")
      );
      return;
    }

    if (moistureValue.value > this.ingredient.weightInGrams) {
      this.$snotify.error(
        this.translations.get("moisture_loss_less_than_weight")
      );
      return;
    }

    // Validation and submission logic
    this.$validator.validateAll().then((result) => {
      if (result) {
        if (
          skipCalculationWarning ||
          this.specifiedSugarAlcohols != 0 ||
          energyValue != 0 ||
          this.showWarningModal
        ) {
          this.showWarningModal = false;
          this.$emit("submit", this.ingredient, nutrients, this.selectedAllergens, this.mayContainText, options);
          return;
        }

        else {
          this.showWarningModal = true;
          return;
        }
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  cancel() {
    this.$emit("cancel");
  }

  /**
   * Method used in create / edit ingredient which pushes empty measurement in
   * the ingredient for the user to populate
   */
  addMeasurement() {
    // we use length as id as it will always be unique locally
    let length = this.ingredient.allMeasurements.length;
    this.ingredient.allMeasurements.push({
      id: length,
      name: "",
      weightInGrams: 0,
    });
  }

  /**
   * Method used in create / edit ingredient which removes specific measurement in the ingredient
   *
   * @param {number} id
   */
  removeMeasurement(id: number) {
    // find the measurement with id from params
    let measurement = this.ingredient.allMeasurements.find((m) => m.id === id);
    if (measurement) {
      // find it's index in the array of measurements
      let index = this.ingredient.allMeasurements.indexOf(measurement);
      // delete it by deleting 1 item on position 'index'
      this.ingredient.allMeasurements.splice(index, 1);
    }
  }

  updateWeightInGrams(){
    switch (this.ingredient.unitOfMeasurement) {
    case IngredientMeasurementEnum.Gram:
    case IngredientMeasurementEnum.Milliliter:
      this.ingredient.weightInGrams = +this.ingredient.quantityInUnitOfMeasurement;
      break;
    case IngredientMeasurementEnum.Ounce:
      this.ingredient.weightInGrams = this.ingredient.quantityInUnitOfMeasurement * OUNCE_TO_GRAMS;
      break;
    case IngredientMeasurementEnum.FluidOunce:
      this.ingredient.weightInGrams = this.ingredient.quantityInUnitOfMeasurement * FLUID_OUNCE_TO_MILLILITERS;
      break;
    default:
      break;
    }
  }

  unitOptions(nutri : NutrientUnitList): UnitOption[]{
    let list = nutri.units
      ? nutri.units.map((u) => ({
        id: u.nutrientUnitId ? u.nutrientUnitId : 0,
        name: u.unit ? u.unit.name : "",
        isDefault:u.isDefault
      }))
      : [];
    if (list) return list;
  }

  get specifiedSugarAlcohols() {
    if (!this.mergedNutrientGroup) {
      return 0;
    }

    const sugarAlcoholNames = [
      'Erythritol',
      'Isomalt',
      'Mannitol',
      'Xylitol',
      'Lactitol',
      'Sorbitol',
      'Maltitol'
    ];

    let sum = 0;

    sugarAlcoholNames.forEach((name) => {
      Object.values(this.mergedNutrientGroup).forEach((nutrientList) => {
        nutrientList.forEach((nutrient) => {
          if (nutrient.name === name) {
            sum += Number(nutrient.value) || 0;
          }
        });
      });
    });

    return Number(sum.toFixed(12));
  }

  get translations() {
    return this.localizationState.translations;
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  //Watcher to detect changes in the nutrientModel
  @Watch('mergedNutrientGroup', { deep: true, immediate: true })
  onNutrientModelChange() {
    const nutrients = this.mergedNutrientGroup;
    this.errorStates = {};

    if(Object.keys(nutrients).length != 0){
      const sugarsAlcohol = nutrients["sugars"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.SugarAlcohols
      );

      const sugarsValue = nutrients["sugars"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.Sugars
      );

      const vitaminA = nutrients["vitamins"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.VitaminA
      );

      const moisture = nutrients["others"].find(
        (nutrient) => nutrient.nutrientType === NutrientType.Moisture
      );

      if(sugarsAlcohol){
        const totalSugarAlcoholValue = this.calculateTotalChildNutrientsValue(sugarsAlcohol.childNutrients);
        this.updatedTotalNutrientValue(sugarsAlcohol, totalSugarAlcoholValue);
      }

      // Handles quantities of the child item being greater than parent -- which should not be possible
      if (sugarsValue.value < sugarsValue.childNutrients[0].value) {
        this.errorStates[sugarsValue.id] = true;
      }

      if(vitaminA.value < this.calculateTotalChildNutrientsValue(vitaminA.childNutrients)){
        this.errorStates[vitaminA.id] = true;
      }

      if (moisture.value > this.ingredient.weightInGrams) {
        this.errorStates[moisture.id] = true;
      }
    }
  }

  @Watch("ingredient", { deep: true, immediate: true })
  onIngredientChanged(value: Ingredient) {
    this.selectedAllergens = value?.allergens ? [...value.allergens] : [];
    this.mayContainText = value?.mayContainText ? { ...value.mayContainText } : {};
  }

  calculateTotalChildNutrientsValue(nutrients: MergedNutrient[]): number {
    let totalValue = 0;
    nutrients.forEach((childNutrient) => {
      totalValue += childNutrient.value;
    });

    return totalValue;
  }

  updatedTotalNutrientValue(element:MergedNutrient, value: number) {
    element.value = value;
  }

  created() {
    this.initializeSourceNutrients();
    // If in edit mode we need to get the density category as an option for the dropdown (if the ingr has it)
    this.densityId = this.ingredient.categoryDensityId
      ? this.ingredient.categoryDensityId
      : "";
    IngredientsRepository.searchCategoryDensities("", this.densityId)
      .then((response: any) => {
        this.densityOptions = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  initializeSourceNutrients(){
    this.nutrientSourceGroup = this.$store.getters.getNutrients;

    if(!this.nutrientSourceGroup){
      this.$store
        .dispatch(NUTRIENTS_FETCH)
        .then((nutrients: NutrientsGroup) => {
          this.nutrientSourceGroup = nutrients;
          this.assignNutrientSource(this.nutrientSourceGroup);
        })
        .catch((error) => {
          if (error.response) {
            this.$snotify.error(
              error.response.data.errorMessages[0].userVisibleMessage
            );
          }
          else this.$snotify.error(this.translations.get("network-error"));
        });
    }
    else{
      this.assignNutrientSource(this.nutrientSourceGroup);
    }
  }

  assignNutrientSource(nutrientSourceGroup : NutrientsGroup){
    this.categories = this.assignCategorys(Object.keys(nutrientSourceGroup));
    this.mergedNutrientGroup = this.mergeNutrientsWithValues(nutrientSourceGroup, this.ingredient);
  }

  assignCategorys(categoryNames: string[]): { id: number; name: string }[] {
    return categoryNames.map((name, index) => ({
      id: index + 1,
      name: name
    }));
  }

  mergeNutrientsWithValues(
    nutrientGroups: NutrientsGroup,
    ingredient: Ingredient
  ): { [category: string]: MergedNutrient[] } {
    const merged: { [category: string]: MergedNutrient[] } = {};

    // Create a map of nutrient values for quick lookup
    const nutrientToNutrientValueMap: { [nutrientId: number]: number | null } = {};
    const nutrientToNutrientUnitMap: { [nutrientId: number]: number | null } = {};

    Object.values(ingredient.nutrients).forEach(({ ingredientNutrient }) => {
      nutrientToNutrientValueMap[ingredientNutrient.nutrientUnit.nutrientId] = ingredientNutrient.value;
      nutrientToNutrientUnitMap[ingredientNutrient.nutrientUnit.nutrientId] = ingredientNutrient.nutrientUnitId;
    });

    // Merge nutrient details with their values
    Object.keys(nutrientGroups).forEach((categoryName) => {
      merged[categoryName] = nutrientGroups[categoryName].map((nutrient) => ({
        ...nutrient,
        value: nutrientToNutrientValueMap[nutrient.id || 0] || 0,
        nutrientUnitId: nutrientToNutrientUnitMap[nutrient.id] || nutrient.units[0].nutrientUnitId,
        childNutrients: nutrient.childNutrients.map((childNutrient) => ({
          ...childNutrient,
          value: nutrientToNutrientValueMap[childNutrient.id || 0] || 0,
          nutrientUnitId: nutrientToNutrientUnitMap[childNutrient.id || 0] || (childNutrient.units && childNutrient.units.length > 0 ? childNutrient.units[0].nutrientUnitId : null)
        })) || null
      }));
    });

    return merged;
  }

  onAllergenValueUpdated(value: SavedAllergen[]) {
    this.selectedAllergens = [...value];
    this.ingredient.allergens = [...value];
  }

  onAllergenAliasesSaved() {
    this.$emit("save-allergen-aliases", this.ingredient.id);
  }

  onMayContainTextUpdated(value: Partial<Record<LabelLanguage, string[]>>) {
    this.mayContainText = { ...value };
    this.ingredient.mayContainText = { ...value };
  }

  onIngredientAiAnalysisApplied(result: MlAnalysisResponse) {
    if (!this.ingredient) {
      return;
    }

    this.ingredient.hasAllergenAnalysis = true;
    this.ingredient.hasAllergen = this.analysisResultHasDetectedAllergen(result);
  }

  onIngredientAiAnalysisRunCompleted() {
    if (!this.pendingAiAutoAnalyzeNotification) {
      return;
    }

    this.pendingAiAutoAnalyzeNotification = false;
    this.$snotify.success("Custom ingredient was successfully saved and analyzed.");
  }

  onPropagationReviewFinished() {
    this.$emit("propagation-review-finished");
  }

  analysisResultHasDetectedAllergen(result: MlAnalysisResponse) {
    if ((result.ingredients || []).length > 0) {
      return result.ingredients.some((ingredientResult) =>
        this.analysisEntryHasDetectedAllergen(ingredientResult.allergens, ingredientResult.overrideMarkers || [])
      );
    }

    return this.analysisEntryHasDetectedAllergen(result.allergens, []);
  }

  analysisEntryHasDetectedAllergen(allergens: Array<{ id?: string | null }>, overrideMarkers: Array<{ isAllergen: boolean }>) {
    return (allergens || []).length > 0 || (overrideMarkers || []).some((marker) => marker.isAllergen);
  }
}
